// @ts-check

/**
 * Bento Meet Max — cropped /max Mobius snippet, driven by page scroll.
 *
 * Same three.js ribbon as max.js, clipped to the Figma tile crop. Progress is
 * the card travelling through the viewport (not the /max 380vh sticky hero).
 * Reduced-motion and missing WebGL keep the static still.
 */

import * as THREE from 'three';
import {RoundedBoxGeometry} from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

(function() {
    'use strict';

    const cards = /** @type {HTMLElement[]} */ (Array.from(
        document.querySelectorAll('[data-bento-max]'),
    ));
    if (!cards.length) {
        return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
        return;
    }

    const clamp01 = (v) => Math.min(1, Math.max(0, v));
    const lerp = (a, b, t) => a + (b - a) * t;

    // Same ribbon as /max at hero progress 0 (top of page).
    const GEOMETRY = {
        fins: 120,
        loopRadius: 1.7,
        finLength: 0.95,
        finWidth: 0.07,
        finDepth: 0.18,
        twistTurns: 1,
        bevel: 0.012,
    };
    const RIBBON_DIAMETER = 4.35;
    const RIPPLE = {
        duration: 3.5,
        spinLag: 0.16,
        rippleAngle: 90,
        spinDrift: 10,
    };
    const SPIN_Z = -10;
    const ENV_ENTRANCE = 0.85;
    const BOOST_MAX = 3.0;
    const VEL_MAX = 1500;
    const VEL_HOLD = 0.35;
    const ATTACK = 14;
    const DECAY = 2.2;

    /**
     * @typedef {object} BentoMaxScene
     * @property {HTMLElement} card
     * @property {HTMLElement} snippet
     * @property {HTMLCanvasElement} canvas
     * @property {THREE.WebGLRenderer} renderer
     * @property {THREE.Scene} scene
     * @property {THREE.OrthographicCamera} camera
     * @property {THREE.InstancedMesh} ribbon
     * @property {THREE.MeshPhysicalMaterial} material
     * @property {{radialDir: THREE.Vector3, base: THREE.Matrix4}[]} finBases
     * @property {THREE.Matrix4} scratchM
     * @property {THREE.Matrix4} scratchRot
     * @property {number} ribbonTime
     * @property {number} ribbonDriftT
     * @property {number} ribbonFrames
     * @property {boolean} live
     * @property {boolean} visible
     */

    /** @type {BentoMaxScene[]} */
    const scenes = [];

    let driftBoost = 1;
    let velPeak = 0;
    let lastScrollY = window.scrollY;
    let lastFrame = performance.now();

    /**
     * Scroll progress of a card through the viewport: 0 entering from below,
     * 1 leaving through the top.
     *
     * @param {HTMLElement} card - The Meet Max tile.
     * @returns {number} 0–1 progress.
     */
    function cardProgress(card) {
        const rect = card.getBoundingClientRect();
        const vh = document.documentElement.clientHeight || 1;
        const travel = vh + rect.height;
        if (travel <= 0) {
            return 0;
        }
        return clamp01((vh - rect.top) / travel);
    }

    /**
     * Build the three.js ribbon for one Meet Max tile.
     *
     * @param {HTMLElement} card - The Meet Max tile.
     * @returns {BentoMaxScene|null} Ready scene, or null if WebGL is unavailable.
     */
    function initScene(card) {
        const snippet = /** @type {HTMLElement|null} */ (card.querySelector('.bento__max-snippet'));
        const canvas = /** @type {HTMLCanvasElement|null} */ (card.querySelector('.bento__max-canvas'));
        if (!snippet || !canvas) {
            return null;
        }

        let gl = null;
        try {
            gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        } catch {
            gl = null;
        }
        if (!gl) {
            return null;
        }

        const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        const renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: coarsePointer,
            alpha: true,
            powerPreference: 'high-performance',
        });
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.05;

        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
        camera.position.set(0, 0, 10);
        scene.add(new THREE.AmbientLight(0xffffff, 0.08));

        const envScene = new THREE.Scene();
        /**
         * Add a drei-style lightformer into the environment scene.
         *
         * @param {'rect'|'ring'} form - Lightformer shape.
         * @param {string} color - Hex colour.
         * @param {number} intensity - Multiplier.
         * @param {number[]} position - xyz.
         * @param {number[]} scale - xyz.
         */
        const former = (form, color, intensity, position, scale) => {
            const geo = form === 'ring' ?
                new THREE.RingGeometry(0.25, 0.5, 64) :
                new THREE.PlaneGeometry(1, 1);
            const mat = new THREE.MeshBasicMaterial({side: THREE.DoubleSide, toneMapped: false});
            mat.color.set(color).multiplyScalar(intensity);
            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.set(position[0], position[1], position[2]);
            mesh.scale.set(scale[0], scale[1], scale[2]);
            mesh.lookAt(0, 0, 0);
            envScene.add(mesh);
        };
        former('rect', '#ffffff', 2.2, [0, 4, 3], [10, 6, 1]);
        former('rect', '#e8edf5', 1.1, [-5, 0, 2], [6, 8, 1]);
        former('rect', '#cdd6e6', 0.7, [5, 1, -2], [6, 8, 1]);
        former('ring', '#ffffff', 1.6, [0, 0, 5], [3, 3, 1]);
        const envTarget = new THREE.WebGLCubeRenderTarget(256);
        envTarget.texture.type = THREE.HalfFloatType;
        const cubeCamera = new THREE.CubeCamera(0.1, 1000, envTarget);
        const autoClear = renderer.autoClear;
        renderer.autoClear = true;
        cubeCamera.update(renderer, envScene);
        renderer.autoClear = autoClear;
        scene.environment = envTarget.texture;

        const {fins, loopRadius, finLength, finWidth, finDepth, twistTurns, bevel} = GEOMETRY;
        const finGeo = new RoundedBoxGeometry(
            finWidth, finLength, finDepth, 2,
            Math.min(bevel, finWidth * 0.49, finDepth * 0.49),
        );
        const material = new THREE.MeshPhysicalMaterial({
            color: '#c4c8cd',
            metalness: 1,
            roughness: 0.22,
            clearcoat: 1,
            clearcoatRoughness: 0.12,
            emissive: '#000000',
            emissiveIntensity: 0.45,
            envMapIntensity: ENV_ENTRANCE,
        });
        material.onBeforeCompile = (shader) => {
            shader.fragmentShader = shader.fragmentShader
                .replace('#include <color_fragment>', '')
                .replace(
                    'vec3 totalEmissiveRadiance = emissive;',
                    'vec3 totalEmissiveRadiance = emissive * vColor.rgb;',
                );
        };

        const ribbon = new THREE.InstancedMesh(finGeo, material, fins);
        /** @type {{radialDir: THREE.Vector3, base: THREE.Matrix4}[]} */
        const finBases = [];
        const white = new THREE.Color(1, 1, 1);
        for (let i = 0; i < fins; i++) {
            const a = (i / fins) * Math.PI * 2;
            const ca = Math.cos(a);
            const sa = Math.sin(a);
            const pos = new THREE.Vector3(ca * loopRadius, sa * loopRadius, 0);
            const tangent = new THREE.Vector3(-sa, ca, 0).normalize();
            const twist = a * twistTurns;
            const width = new THREE.Vector3(ca, sa, 0)
                .multiplyScalar(Math.cos(twist))
                .addScaledVector(new THREE.Vector3(0, 0, 1), Math.sin(twist))
                .normalize();
            const depth = new THREE.Vector3().crossVectors(tangent, width).normalize();
            const base = new THREE.Matrix4().makeBasis(tangent, width, depth).setPosition(pos);
            finBases.push({radialDir: new THREE.Vector3(ca, sa, 0), base});
            ribbon.setMatrixAt(i, base);
            ribbon.setColorAt(i, white);
        }
        if (ribbon.instanceColor) {
            ribbon.instanceColor.needsUpdate = true;
        }
        scene.add(ribbon);

        canvas.addEventListener('webglcontextlost', (e) => e.preventDefault(), false);

        /** @type {BentoMaxScene} */
        const item = {
            card,
            snippet,
            canvas,
            renderer,
            scene,
            camera,
            ribbon,
            material,
            finBases,
            scratchM: new THREE.Matrix4(),
            scratchRot: new THREE.Matrix4(),
            ribbonTime: 0,
            ribbonDriftT: 0,
            ribbonFrames: 0,
            live: false,
            visible: false,
        };
        resizeScene(item);
        if (typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(() => resizeScene(item)).observe(snippet);
        }
        return item;
    }

    /**
     * Match the WebGL buffer to the cropped snippet box.
     *
     * @param {BentoMaxScene} item - Scene to size.
     */
    function resizeScene(item) {
        const w = item.snippet.offsetWidth;
        const h = item.snippet.offsetHeight;
        if (w < 1 || h < 1) {
            return;
        }
        const ratio = window.devicePixelRatio || 1;
        const isMobile = window.innerWidth < 768;
        const multiplier = isMobile ? 2 : 1.75;
        const maxRatio = isMobile ? 4 : 3;
        const pixelRatio = Math.min(ratio * multiplier, maxRatio, 2048 / Math.max(w, h));
        item.renderer.setPixelRatio(pixelRatio);
        item.renderer.setSize(w, h, false);
        item.camera.left = -w / 2;
        item.camera.right = w / 2;
        item.camera.top = h / 2;
        item.camera.bottom = -h / 2;
        // Fill the (already sized) crop box; CSS owns how big the ring reads.
        item.camera.zoom = (Math.min(w, h) * 0.76) / RIBBON_DIAMETER;
        item.camera.updateProjectionMatrix();
    }

    /**
     * Same ripple as /max at the top of the page, plus a slight scroll rise.
     *
     * @param {BentoMaxScene} item - Scene to tick.
     * @param {number} dt - Frame delta, seconds.
     * @param {number} p - Scroll progress 0–1.
     */
    function updateScene(item, dt, p) {
        item.material.envMapIntensity = ENV_ENTRANCE;
        const DEG = Math.PI / 180;
        item.ribbonTime += dt;
        item.ribbonDriftT += dt * driftBoost;

        const {fins} = GEOMETRY;
        const u = ((item.ribbonTime / RIPPLE.duration) % 1 + 1) % 1;
        const peak = RIPPLE.rippleAngle * DEG;
        const drift = item.ribbonDriftT * (RIPPLE.spinDrift * DEG);
        const W = RIPPLE.spinLag;

        item.ribbon.rotation.z = item.ribbonDriftT * (SPIN_Z * DEG);

        for (let i = 0; i < fins; i++) {
            const bp = i / fins;
            let dd = Math.abs(bp - u);
            dd = Math.min(dd, 1 - dd);
            const g = Math.exp(-((dd / W) * (dd / W)) * 4);
            const tilt = peak * g + drift;
            item.scratchRot.makeRotationX(tilt);
            item.scratchM.copy(item.finBases[i].base).multiply(item.scratchRot);
            item.ribbon.setMatrixAt(i, item.scratchM);
        }
        item.ribbon.instanceMatrix.needsUpdate = true;
        item.renderer.render(item.scene, item.camera);

        item.ribbonFrames += 1;
        if (!item.live && item.ribbonFrames >= 3) {
            item.live = true;
            item.card.classList.add('is-live');
        }

        item.snippet.style.setProperty('--bento-max-shift', `${lerp(0, -2, p).toFixed(2)}%`);
    }

    /**
     * Shared rAF loop: scroll velocity plus every visible tile.
     *
     * @param {number} now - rAF timestamp.
     */
    function frame(now) {
        requestAnimationFrame(frame);
        const rawDt = (now - lastFrame) / 1000;
        lastFrame = now;
        const dt = Math.min(rawDt, 0.05);
        if (document.visibilityState !== 'visible') {
            return;
        }

        const cur = window.scrollY;
        const instVel = Math.abs(cur - lastScrollY) / Math.max(dt, 0.001);
        lastScrollY = cur;
        velPeak = Math.min(VEL_MAX, Math.max(instVel, velPeak * Math.exp(-dt / VEL_HOLD)));
        const target = 1 + Math.min(1, velPeak / VEL_MAX) * (BOOST_MAX - 1);
        const rate = target > driftBoost ? ATTACK : DECAY;
        driftBoost += (target - driftBoost) * (1 - Math.exp(-dt * rate));

        for (let i = 0; i < scenes.length; i++) {
            const item = scenes[i];
            if (!item.visible) {
                continue;
            }
            updateScene(item, dt, cardProgress(item.card));
        }
    }

    for (let i = 0; i < cards.length; i++) {
        const item = initScene(cards[i]);
        if (item) {
            scenes.push(item);
        }
    }

    if (scenes.length === 0) {
        return;
    }

    if (typeof IntersectionObserver === 'undefined') {
        for (let i = 0; i < scenes.length; i++) {
            scenes[i].visible = true;
        }
    } else {
        const io = new IntersectionObserver((entries) => {
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const item = scenes.find((s) => s.card === entry.target);
                if (item) {
                    item.visible = entry.isIntersecting;
                }
            }
        }, {rootMargin: '20% 0px'});
        for (let i = 0; i < scenes.length; i++) {
            io.observe(scenes[i].card);
            const rect = scenes[i].card.getBoundingClientRect();
            const vh = document.documentElement.clientHeight || 0;
            scenes[i].visible = rect.bottom > 0 && rect.top < vh;
        }
    }

    window.addEventListener('resize', () => {
        for (let i = 0; i < scenes.length; i++) {
            resizeScene(scenes[i]);
        }
    });

    requestAnimationFrame(frame);
})();
